import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, closeDialog, openDialog} from 'platform/components';

import {useState} from 'react';
import {useMatch} from 'react-router-dom';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {workshopRoutes, testIds, warehouseRoutes} from '@dms/routes';
import {Main, refreshDmsDatagrid, usePermissions, WorkItemsListCard} from '@dms/shared';
import {CatalogueFolder, LabourCatalogItemFrom} from '@dms/shared-aftersales';

import {buildArray, useNavigate} from 'shared/web';

import {DirectSalesOverview} from './(sections)/DirectSalesOverview/DirectSalesOverview';
import {ServiceCaseOverview} from './(sections)/ServiceCaseoverview/ServiceCaseOverview';
import {ServiceOrderOverview} from './(sections)/ServiceOrderOverview/ServiceOrderOverview';

export function WorkshopOverview() {
  const navigate = useNavigate();
  const labourOperationSettingsMatch = useMatch(workshopRoutes.labourOperationSettings);

  const [activeLabourtFolder, setActiveLabourFolder] = useState<CatalogueFolder | undefined>();

  const [canCreateCatalogueItem] = usePermissions({permissionKeys: ['createCatalogueItem']});

  const onCreateNewLabourClick = () => {
    if (isNil(activeLabourtFolder)) {
      return;
    }

    openDialog(
      <LabourCatalogItemFrom
        onClose={() => closeDialog('createNewItemDialog')}
        onSubmitted={() => refreshDmsDatagrid('labour-catalog-items', {deselectAll: true})}
        catalogueFolder={activeLabourtFolder}
        data-testid={testIds.workshop.serviceCases('createNewLabour')}
      />,
      {
        id: 'createNewItemDialog',
        title: i18n.t('general.labels.createNew'),
        scrollBehavior: 'outside',
      }
    );
  };

  const tabs = [
    {
      id: 'serviceCases',
      title: i18n.t('page.workshop.labels.serviceCases'),
      content: <ServiceCaseOverview />,
      href: workshopRoutes.serviceCases,
    },
    {
      id: 'serviceOrders',
      title: i18n.t('page.workshop.labels.serviceOrders'),
      content: <ServiceOrderOverview />,
      href: workshopRoutes.serviceOrders,
    },
    {
      id: 'directSales',
      title: i18n.t('page.workshop.labels.directSales'),
      content: <DirectSalesOverview />,
      href: workshopRoutes.directSales,
    },
    {
      id: 'labourOperationSettings',
      title: i18n.t('page.workshop.labels.labourOperationSettings'),
      content: (
        <WorkItemsListCard
          data-testid={testIds.workshop.labourOperationSettings()}
          createOverride={{
            activeFolder: activeLabourtFolder,
            setActiveFolder: setActiveLabourFolder,
          }}
        />
      ),
      href: workshopRoutes.labourOperationSettings,
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('module.workshop.title')} />
      <Main
        isFullHeight
        tabs={tabs}
        actions={buildArray<Action>()
          .whenNot(labourOperationSettingsMatch, {
            type: 'button',
            title: i18n.t('page.warehouse.labels.newDirectSale'),
            variant: 'secondary',
            onClick: () => navigate(warehouseRoutes.directSalesCreate),
          })
          .whenNot(labourOperationSettingsMatch, {
            type: 'button',
            title: i18n.t('page.workshop.labels.newServiceCase'),
            onClick: () => navigate(workshopRoutes.createServiceCase),
          })
          .when(labourOperationSettingsMatch, {
            type: 'buttonWithTooltip',
            title: i18n.t('entity.workshopServicePackages.actions.createCatalogueItem'),
            isDisabled: isNil(activeLabourtFolder) || !canCreateCatalogueItem,
            tooltip: {
              isDisabled: canCreateCatalogueItem,
              label: i18n.t('general.labels.noPermission'),
            },
            onClick: onCreateNewLabourClick,
          })}
        data-testid={testIds.workshop.serviceCases('createServiceCase')}
      />
    </>
  );
}
